import React, { useEffect, useRef, useState } from "react";
import { CircleAlert } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useSubjects } from "../SubjectContext";
import { trackHealthSectionSkipped } from "../lib/analytics";
import { EXTENDED_SECTIONS, sectionStatus, showHealthContextRow } from "../lib/healthSections";
import HealthSectionForm from "./HealthSectionForm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/**
 * Health Profile links on the case-for line. Each section opens its own dialog.
 * Hidden for one-off cases and until tier A is complete.
 */
export default function HealthContextRow({
  bonusAvailable = false,
  initialExtended = {},
  onSaved,
  children,
}) {
  const { t } = useLanguage();
  const { activeSubject, caseOneOff } = useSubjects();
  const subjectKey = caseOneOff ? "oneoff" : activeSubject?.subject_id || "";
  const [openSection, setOpenSection] = useState(null);
  const savedRef = useRef(false);

  useEffect(() => {
    savedRef.current = false;
    setOpenSection(null);
  }, [subjectKey]);

  const openHealthSection = (key) => {
    if (!showHealthContextRow(activeSubject, caseOneOff)) return;
    if (!EXTENDED_SECTIONS.includes(key)) return;
    savedRef.current = false;
    setOpenSection(key);
  };

  const closeSection = (skipped) => {
    if (skipped && openSection && !savedRef.current) {
      trackHealthSectionSkipped({
        section: openSection,
        surface: "row",
        subjectKind: activeSubject?.kind,
      });
    }
    savedRef.current = false;
    setOpenSection(null);
  };

  if (!showHealthContextRow(activeSubject, caseOneOff)) {
    if (typeof children === "function") {
      return children({
        links: null,
        bonus: null,
        dialog: null,
        openHealthSection,
      });
    }
    return null;
  }

  const showBonus = activeSubject.kind === "self" && Boolean(bonusAvailable);

  const links = (
    <div
      className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm md:text-base"
      data-testid="health-context-row"
    >
      <span className="text-muted-foreground">{t("healthProfile.contextFor")}</span>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        {EXTENDED_SECTIONS.map((key, index) => {
          const status = sectionStatus(activeSubject, key);
          const label = t(`healthProfile.section.${key}`);
          return (
            <React.Fragment key={key}>
              {index > 0 && (
                <span aria-hidden="true" className="text-muted-foreground">
                  ·
                </span>
              )}
              <button
                type="button"
                className="inline-flex items-center gap-1 text-primary hover:underline"
                data-testid={`health-context-${key}`}
                data-status={status}
                aria-label={
                  status === "answered"
                    ? label
                    : `${label}, ${t("subjects.notProvided")}`
                }
                onClick={() => openHealthSection(key)}
              >
                <span>{status === "answered" ? `${label} ✓` : label}</span>
                {status !== "answered" && (
                  <CircleAlert
                    className="h-4 w-4 shrink-0 text-amber-600"
                    aria-hidden="true"
                    data-testid={`health-context-${key}-missing`}
                  />
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  const bonus = showBonus ? (
    <p className="text-xs text-foreground" data-testid="health-context-bonus">
      {t("query.onboardingBonusCta")}
    </p>
  ) : null;

  const dialog = (
    <Dialog
      open={Boolean(openSection)}
      onOpenChange={(open) => {
        if (!open) closeSection(true);
      }}
    >
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="health-section-dialog"
      >
        {openSection && (
          <>
            <DialogHeader>
              <DialogTitle>{t(`healthProfile.section.${openSection}`)}</DialogTitle>
              <DialogDescription>
                {t(`healthProfile.sectionDesc.${openSection}`)}
              </DialogDescription>
            </DialogHeader>
            <HealthSectionForm
              section={openSection}
              subjectId={activeSubject.subject_id}
              initialExtended={initialExtended}
              analytics={{
                surface: "row",
                subjectKind: activeSubject.kind,
              }}
              onSaved={async (data) => {
                savedRef.current = true;
                setOpenSection(null);
                await onSaved?.(data);
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  if (typeof children === "function") {
    return children({ links, bonus, dialog, openHealthSection });
  }

  return (
    <div className="space-y-1">
      {bonus}
      {links}
      {dialog}
    </div>
  );
}
